import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  dimensionMetrics,
  ellipticalArcPathD,
  flareGeometry,
  hitMarkAt,
  linearGradientAxis,
  markFontCss,
  markStrokeSvg,
  normalizeFreePage,
  normalizeMarkFontFamily,
  normalizeMarkFontWeight,
  normalizeMarkTextAlign,
  normalizeMarkGradient,
  normalizeWidthPoints,
  variableWidthOutline,
  regularPolygonPoints,
  rotatedImageLayout,
  sortSlotsByLayer,
  starPolygonPoints,
  textMarkBoxWidth,
  textMarkFillColor,
  textMarkOutlineCss,
} from "./skillAgentMoodboardLayout";
import { peekMoodboardAssetBlobUrl } from "./moodboardAssetBlobCache";
import { slotClipLocalGeometry } from "./skillAgentMoodboardTools";

/**
 * Read-only artboard for board workspace (inactive pages).
 * Must match live canvas framing (coverFit / marks) so switching pages
 * does not look like figures vanish or photos resize.
 */
export default function MoodboardArtboardPreview({
  page,
  assets = [],
  pageLabel = "",
  active = false,
  onSelect,
  fetchBlob,
  chromeSeed = null,
  /** Bajo la mesa activa: fotos + figuras SVG (textos los pinta el overlay). */
  underlay = false,
  /** Ids a no pintar en underlay (p.ej. casilla en drag → evita fantasma). */
  hideSlotIds = null,
  /** Mano / zoom: no activar mesa ni seleccionar al arrastrar el tablero. */
  boardNavLock = false,
}) {
  // Solo pintar lo que hay en page.marks — no re-sembrar cabecera (duplicaba).
  const normalized = useMemo(() => normalizeFreePage(page), [page]);
  const slots = useMemo(
    () => sortSlotsByLayer(normalized.slots || []),
    [normalized.slots],
  );
  const marks = normalized.marks || [];
  const assetById = useMemo(() => {
    const m = new Map();
    (assets || []).forEach((a) => m.set(Number(a.id), a));
    return m;
  }, [assets]);

  const hitAt = (clientX, clientY, el) => {
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 1) || !(rect.height > 1)) return {};
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    // Figuras / cabecera-pie (marcas) encima de fotos
    const mark = hitMarkAt(marks, nx, ny);
    if (mark?.id != null) return { markId: mark.id };
    const ordered = [...slots].sort(
      (a, b) => (Number(b.z) || 0) - (Number(a.z) || 0),
    );
    for (const s of ordered) {
      const x = Number(s.x) || 0;
      const y = Number(s.y) || 0;
      const w = Number(s.w) || 0;
      const h = Number(s.h) || 0;
      if (nx >= x && nx <= x + w && ny >= y && ny <= y + h) {
        return { slotId: s.id };
      }
    }
    return {};
  };

  const inert = underlay || boardNavLock;

  return (
    <Box
      role={inert ? undefined : "button"}
      tabIndex={inert ? undefined : 0}
      onClick={
        inert
          ? undefined
          : (e) => {
              e.stopPropagation();
              onSelect?.(hitAt(e.clientX, e.clientY, e.currentTarget));
            }
      }
      onKeyDown={
        inert
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect?.({});
              }
            }
      }
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#f3f3f3",
        // visible: marcas sacadas de la mesa se siguen viendo al cambiar de página
        overflow: "visible",
        cursor: inert ? "inherit" : "pointer",
        pointerEvents: inert ? "none" : "auto",
        containerType: "size",
        containerName: "moodboard-preview",
        outline: underlay
          ? "none"
          : active
            ? (t) => `2px solid ${t.palette.primary.main}`
            : "1px solid rgba(15,23,42,0.12)",
        outlineOffset: 0,
        boxShadow: underlay
          ? "none"
          : active
            ? (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.25)}`
            : "0 1px 3px rgba(15,23,42,0.08)",
        "&:hover": inert
          ? undefined
          : {
              outlineColor: (t) => t.palette.primary.main,
            },
      }}
    >
      {slots
        .filter((slot) => {
          if (!hideSlotIds?.size) return true;
          return !hideSlotIds.has(String(slot.id));
        })
        .map((slot) => (
          <PreviewSlot
            key={slot.id}
            slot={slot}
            asset={
              slot.assetId != null ? assetById.get(Number(slot.assetId)) : null
            }
            fetchBlob={fetchBlob}
          />
        ))}
      {/* Marcas en orden del array (Traer al frente). Underlay: opacity 0 —
          las pinta MarksOverlay (si las formas seguían visibles, tapaban texto). */}
      {marks.map((m, markIdx) => {
        const layerZ = 8 + markIdx;
        const underlayHide = underlay ? 0 : 1;
        if (m?.type === "text") {
          if (!String(m.text || "").trim()) return null;
          const fs = Number(m.fontSize) || 14;
          const boxW = textMarkBoxWidth(m);
          const rot = Number(m.rotation) || 0;
          const fill = textMarkFillColor(m);
          return (
            <Box
              key={`txt-${m.id}`}
              component="span"
              sx={{
                position: "absolute",
                left: `${m.x * 100}%`,
                top: `${m.y * 100}%`,
                width: `${boxW * 100}%`,
                boxSizing: "border-box",
                transform: rot ? `rotate(${rot}deg)` : undefined,
                transformOrigin: "center center",
                color: fill,
                fontWeight: normalizeMarkFontWeight(m.fontWeight),
                fontFamily: markFontCss(normalizeMarkFontFamily(m.fontFamily)),
                fontSize: `${((fs / 14) * 1.85).toFixed(3)}cqw`,
                textAlign: normalizeMarkTextAlign(m.textAlign),
                lineHeight: 1.2,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                ...textMarkOutlineCss(m, Math.max(10, fs * 1.35)),
                pointerEvents: "none",
                zIndex: layerZ,
                opacity: underlayHide,
              }}
            >
              {String(m.text || "")}
            </Box>
          );
        }
        return (
          <svg
            key={m.id}
            width="100%"
            height="100%"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            overflow="visible"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "visible",
              zIndex: layerZ,
              opacity: underlayHide,
            }}
          >
            <PreviewMark mark={m} idPrefix={`p${String(pageLabel || "x")}`} />
          </svg>
        );
      })}

      {!underlay && pageLabel ? (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: 6,
            top: 4,
            zIndex: 9,
            px: 0.6,
            py: 0.15,
            borderRadius: 0.75,
            bgcolor: "rgba(15,23,42,0.72)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.65rem",
            pointerEvents: "none",
          }}
        >
          {pageLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

function PreviewSlot({ slot, asset, fetchBlob }) {
  const boxRef = useRef(null);
  const [url, setUrl] = useState(() =>
    asset?.id ? peekMoodboardAssetBlobUrl(asset.id) : "",
  );
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let cancelled = false;
    if (!asset?.id || typeof fetchBlob !== "function") {
      setUrl("");
      return undefined;
    }
    const peeked = peekMoodboardAssetBlobUrl(asset.id);
    if (peeked) setUrl(peeked);
    else setNat(null);
    (async () => {
      try {
        const next = await fetchBlob(asset.id);
        if (cancelled) return;
        setUrl(typeof next === "string" ? next : "");
      } catch {
        if (!cancelled) setUrl("");
      }
    })();
    return () => {
      cancelled = true;
      // Cache compartida: no revoke al desmontar (evita parpadeo al cambiar mesa)
    };
  }, [asset?.id, fetchBlob]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      // clientWidth/Height = layout size (pre board scale), same as AssetThumb
      setBoxPx({ w: el.clientWidth, h: el.clientHeight });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url]);

  const rotation = Number(slot.rotation) || 0;
  // Rotación en el contenedor (como el editor); layout de imagen sin rotar
  const fitted =
    nat && boxPx.w > 1 && boxPx.h > 1
      ? rotatedImageLayout(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom: Number(slot.zoom) || 1,
            panX: slot.panX ?? 0.5,
            panY: slot.panY ?? 0.5,
          },
          0,
        )
      : null;

  const clipGeom = slotClipLocalGeometry(slot);
  const isCircle = !clipGeom && slot.shape === "circle";
  const clipPathId = `prev-clip-${String(slot.id || "x").replace(/\W/g, "")}`;
  return (
    <Box
      ref={boxRef}
      sx={{
        position: "absolute",
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        borderRadius: isCircle ? "50%" : 0.5,
        overflow: "hidden",
        // Igual que el lienzo vivo: sin fondo gris (cutouts / sin fondo se ven bien)
        bgcolor: slot.assetId != null ? "transparent" : "#ebebeb",
        pointerEvents: "none",
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: "center center",
        ...(clipGeom
          ? {
              clipPath: `url(#${clipPathId})`,
              WebkitClipPath: `url(#${clipPathId})`,
            }
          : null),
      }}
    >
      {clipGeom ? (
        <svg width={0} height={0} aria-hidden style={{ position: "absolute" }}>
          <defs>
            <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
              {clipGeom.kind === "ellipse" ? (
                <ellipse
                  cx={clipGeom.cx}
                  cy={clipGeom.cy}
                  rx={clipGeom.rx}
                  ry={clipGeom.ry}
                />
              ) : clipGeom.kind === "path" ? (
                <path d={clipGeom.d} />
              ) : (
                <rect
                  x={clipGeom.x}
                  y={clipGeom.y}
                  width={clipGeom.w}
                  height={clipGeom.h}
                  rx={clipGeom.rx || 0}
                  ry={clipGeom.ry || clipGeom.rx || 0}
                />
              )}
            </clipPath>
          </defs>
        </svg>
      ) : null}
      {url ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            transform:
              slot.flipX || slot.flipY
                ? `scale(${slot.flipX ? -1 : 1}, ${slot.flipY ? -1 : 1})`
                : undefined,
          }}
        >
          <Box
            component="img"
            src={url}
            alt=""
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img?.naturalWidth && img?.naturalHeight) {
                setNat({ w: img.naturalWidth, h: img.naturalHeight });
              }
            }}
            sx={
              fitted
                ? {
                    position: "absolute",
                    left: fitted.x,
                    top: fitted.y,
                    width: fitted.w,
                    height: fitted.h,
                    objectFit: "fill",
                    display: "block",
                  }
                : {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: 0,
                    display: "block",
                  }
            }
          />
        </Box>
      ) : null}
    </Box>
  );
}

function PreviewMark({ mark, idPrefix = "p" }) {
  if (!mark) return null;
  const ink = mark.strokeColor || mark.color || "#111";
  const grad = normalizeMarkGradient(mark.gradient, ink);
  // Prefijo por página: evita chocar ids SVG entre mesas (mismo mark.id)
  const gradId = grad
    ? `mb-grad-${String(idPrefix).replace(/\W/g, "")}-${String(mark.id || "x").replace(/\W/g, "")}`
    : null;
  const solidFill =
    mark.fill && mark.fill !== "none" && mark.fill !== "transparent"
      ? mark.fill
      : "none";
  const fill = gradId ? `url(#${gradId})` : solidFill;
  // Misma escala que el canvas activo (sin non-scaling-stroke: ese modo
  // interpreta el grosor en px de pantalla → bordes ~invisibles al desactivar).
  const stroke = markStrokeSvg(mark.stroke);
  const opacity = Number.isFinite(Number(mark.opacity))
    ? Math.max(0.05, Math.min(1, Number(mark.opacity)))
    : 1;
  const common = {
    fill,
    stroke: ink,
    strokeWidth: stroke,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    opacity,
  };
  const axis = grad ? linearGradientAxis(grad.angle) : null;
  const gradDefs = gradId ? (
    <defs>
      {grad.kind === "radial" ? (
        <radialGradient
          id={gradId}
          gradientUnits="objectBoundingBox"
          cx="0.5"
          cy="0.5"
          r="0.7"
        >
          <stop offset="0" stopColor={grad.from} />
          <stop offset="1" stopColor={grad.to} />
        </radialGradient>
      ) : (
        <linearGradient
          id={gradId}
          gradientUnits="objectBoundingBox"
          x1={axis.x1}
          y1={axis.y1}
          x2={axis.x2}
          y2={axis.y2}
        >
          <stop offset="0" stopColor={grad.from} />
          <stop offset="1" stopColor={grad.to} />
        </linearGradient>
      )}
    </defs>
  ) : null;
  const withGrad = (el) =>
    gradDefs ? (
      <g>
        {gradDefs}
        {el}
      </g>
    ) : (
      el
    );

  if (
    (mark.type === "path" || mark.type === "textpath") &&
    Array.isArray(mark.points) &&
    mark.points.length
  ) {
    const d = mark.points
      .map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`)
      .join(" ");
    if (mark.type === "textpath") {
      return (
        <path
          d={d}
          fill="none"
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />
      );
    }
    const closedD = mark.closed && mark.points.length >= 3 ? `${d} Z` : d;
    const widthPts = normalizeWidthPoints(mark.widthPoints);
    if (widthPts.length) {
      const outline = variableWidthOutline(mark.points, mark.stroke, widthPts, {
        closed: Boolean(mark.closed),
      });
      const od =
        outline && outline.length >= 3
          ? outline.map((p) => `${p.x},${p.y}`).join(" ")
          : null;
      const centerFill = mark.closed && fill !== "none" ? fill : "none";
      return withGrad(
        <g opacity={opacity}>
          {centerFill !== "none" ? (
            <path d={closedD} fill={centerFill} stroke="none" />
          ) : null}
          {od ? (
            <polygon points={od} fill={ink} stroke="none" />
          ) : (
            <path d={closedD} fill="none" stroke={ink} strokeWidth={stroke} />
          )}
        </g>,
      );
    }
    return withGrad(
      <path
        d={closedD}
        {...common}
        fill={fill === "none" && !gradId ? "none" : fill}
      />,
    );
  }
  if (mark.type === "line" || mark.type === "arrow") {
    return (
      <g opacity={opacity}>
        <line
          x1={mark.x1}
          y1={mark.y1}
          x2={mark.x2}
          y2={mark.y2}
          stroke={ink}
          strokeWidth={stroke}
          strokeLinecap="round"
        />
      </g>
    );
  }
  if (mark.type === "dimension") {
    const d = dimensionMetrics(mark);
    return (
      <g opacity={opacity}>
        <line
          x1={d.x1}
          y1={d.y1}
          x2={d.x2}
          y2={d.y2}
          stroke={ink}
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <line
          x1={d.t1a.x}
          y1={d.t1a.y}
          x2={d.t1b.x}
          y2={d.t1b.y}
          stroke={ink}
          strokeWidth={stroke * 0.9}
        />
        <line
          x1={d.t2a.x}
          y1={d.t2a.y}
          x2={d.t2b.x}
          y2={d.t2b.y}
          stroke={ink}
          strokeWidth={stroke * 0.9}
        />
        <text
          x={d.labelX}
          y={d.labelY}
          fill={ink}
          fontSize={0.024}
          fontWeight={700}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {d.label}
        </text>
      </g>
    );
  }
  if (mark.type === "rect" || mark.type === "roundrect") {
    const rx =
      mark.type === "roundrect"
        ? Math.min(
            Number(mark.rx) || 0.04,
            Math.min(Number(mark.w) || 0, Number(mark.h) || 0) / 2,
          )
        : 0;
    return withGrad(
      <rect
        x={mark.x}
        y={mark.y}
        width={Math.max(Number(mark.w) || 0, 0.001)}
        height={Math.max(Number(mark.h) || 0, 0.001)}
        rx={rx || undefined}
        ry={rx || undefined}
        {...common}
      />,
    );
  }
  if (mark.type === "ellipse") {
    return withGrad(
      <ellipse
        cx={mark.x + mark.w / 2}
        cy={mark.y + mark.h / 2}
        rx={Math.max(Math.abs(mark.w / 2), 0.001)}
        ry={Math.max(Math.abs(mark.h / 2), 0.001)}
        {...common}
      />,
    );
  }
  if (mark.type === "arc") {
    return (
      <path
        d={ellipticalArcPathD(mark)}
        fill="none"
        stroke={ink}
        strokeWidth={stroke}
        strokeLinecap="round"
        opacity={opacity}
      />
    );
  }
  if (mark.type === "polygon" || mark.type === "star") {
    const verts =
      mark.type === "star"
        ? starPolygonPoints({
            x: mark.x,
            y: mark.y,
            w: mark.w,
            h: mark.h,
            tips: mark.tips,
            innerRatio: mark.innerRatio,
          })
        : regularPolygonPoints({
            x: mark.x,
            y: mark.y,
            w: mark.w,
            h: mark.h,
            sides: mark.sides,
          });
    return withGrad(
      <polygon
        points={verts.map((p) => `${p.x},${p.y}`).join(" ")}
        {...common}
      />,
    );
  }
  if (mark.type === "grid") {
    const cols = Math.max(2, Number(mark.cols) || 4);
    const rows = Math.max(2, Number(mark.rows) || 4);
    const lines = [];
    for (let c = 0; c <= cols; c += 1) {
      const gx = mark.x + (mark.w * c) / cols;
      lines.push(
        <line
          key={`gc${c}`}
          x1={gx}
          y1={mark.y}
          x2={gx}
          y2={mark.y + mark.h}
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />,
      );
    }
    for (let r = 0; r <= rows; r += 1) {
      const gy = mark.y + (mark.h * r) / rows;
      lines.push(
        <line
          key={`gr${r}`}
          x1={mark.x}
          y1={gy}
          x2={mark.x + mark.w}
          y2={gy}
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />,
      );
    }
    return <g>{lines}</g>;
  }
  if (mark.type === "polarGrid") {
    const rings = Math.max(2, Number(mark.rings) || 4);
    const rays = Math.max(3, Number(mark.rays) || 8);
    const cx = mark.x + mark.w / 2;
    const cy = mark.y + mark.h / 2;
    const rx = Math.max(mark.w / 2, 0.001);
    const ry = Math.max(mark.h / 2, 0.001);
    const els = [];
    for (let r = 1; r <= rings; r += 1) {
      const t = r / rings;
      els.push(
        <ellipse
          key={`pr${r}`}
          cx={cx}
          cy={cy}
          rx={rx * t}
          ry={ry * t}
          fill="none"
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />,
      );
    }
    for (let i = 0; i < rays; i += 1) {
      const a = (i / rays) * Math.PI * 2;
      els.push(
        <line
          key={`py${i}`}
          x1={cx}
          y1={cy}
          x2={cx + rx * Math.cos(a)}
          y2={cy + ry * Math.sin(a)}
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />,
      );
    }
    return <g>{els}</g>;
  }
  if (mark.type === "flare") {
    const g = flareGeometry(mark);
    const els = [
      <ellipse
        key="fc"
        cx={g.cx}
        cy={g.cy}
        rx={g.coreRx}
        ry={g.coreRy}
        fill={ink}
        stroke="none"
        opacity={opacity}
      />,
    ];
    g.rings.forEach((ring, i) => {
      els.push(
        <ellipse
          key={`fr${i}`}
          cx={g.cx}
          cy={g.cy}
          rx={ring.rx}
          ry={ring.ry}
          fill="none"
          stroke={ink}
          strokeWidth={stroke}
          opacity={opacity}
        />,
      );
    });
    g.rays.forEach((ray, i) => {
      els.push(
        <polygon
          key={`fy${i}`}
          points={`${ray.tip.x},${ray.tip.y} ${ray.b1.x},${ray.b1.y} ${ray.b2.x},${ray.b2.y}`}
          fill={ink}
          stroke="none"
          opacity={opacity * (ray.long ? 0.62 : 0.38)}
        />,
      );
    });
    return <g>{els}</g>;
  }
  if (mark.type === "text") {
    const ff = normalizeMarkFontFamily(mark.fontFamily);
    const ta = normalizeMarkTextAlign(mark.textAlign);
    const fs = (Number(mark.fontSize) || 14) / 900;
    const fill = textMarkFillColor(mark);
    const sw = Number(mark.stroke) > 0 ? markStrokeSvg(mark.stroke) * 0.55 : 0;
    return (
      <text
        x={mark.x}
        y={mark.y + fs}
        fill={fill}
        stroke={sw > 0 ? ink : "none"}
        strokeWidth={sw > 0 ? sw : undefined}
        paintOrder={sw > 0 ? "stroke fill" : undefined}
        fontSize={fs}
        fontFamily={markFontCss(ff)}
        fontWeight={normalizeMarkFontWeight(mark.fontWeight)}
        textAnchor={
          ta === "center" ? "middle" : ta === "right" ? "end" : "start"
        }
        opacity={opacity}
        style={{ userSelect: "none" }}
      >
        {String(mark.text || "").slice(0, 80)}
      </text>
    );
  }
  return null;
}
